import { trimStrings } from "@pulse-ui/formatting";
import { api } from "@/services/api";
import { DELETE, POST } from "@/services/consts";

export const alertManagerApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    // Fetch alert schemas
    fetchAlertSchemas: builder.query<any, void>({
      query: () => "/api/alert/v1/check/forms",
    }),

    // Fetch all checks
    fetchAllChecks: builder.query<any, void>({
      query: () => "/api/alert/v1/instances/all",
      providesTags: ["AlertChecks"],
    }),

    // Fetch portfolios
    fetchPortfolios: builder.query<any, void>({
      query: () => "/api/alert/v1/portfolios",
    }),

    // Fetch slack channels
    fetchSlackChannels: builder.query<any, void>({
      query: () => "/api/alert/v1/check/destinations/slack",
    }),

    // Fetch telegram channels
    fetchTelegramChannels: builder.query<any, void>({
      query: () => "/api/alert/v1/check/destinations/telegram",
    }),

    // Fetch sms channels
    fetchSmsChannels: builder.query<any, void>({
      query: () => "/api/alert/v1/check/destinations/sms",
    }),

    // Fetch group destinations
    fetchGroupDestinations: builder.query<any, void>({
      query: () => "/api/alert/v1/check/group_destinations",
      providesTags: ["AlertChecks"],
    }),

    // Start default config for an account or portfolio
    startDefaultConfig: builder.mutation<any, { account_id: string }>({
      query: ({ account_id }) => ({
        url: `/api/alert/v1/check/default/${account_id}/start`,
        method: POST,
        responseHandler: (response: Response) => response.text(),
      }),
      invalidatesTags: ["AlertChecks"],
    }),

    // Start alert
    startAlert: builder.mutation<any, { payload: Record<string, any> }>({
      query: ({ payload }) => ({
        url: `/api/alert/v1/check/start`,
        method: POST,
        body: trimStrings(payload),
      }),
      invalidatesTags: ["AlertChecks"],
    }),

    // Test fire alert
    testFireAlert: builder.mutation<any, { check_name: string }>({
      query: ({ check_name }) => ({
        url: `/api/alert/v1/check/single_handle`,
        method: POST,
        body: { check_name: check_name.trim() },
        responseHandler: (response: Response) => response.text(),
      }),
    }),

    // Stop (delete) alert — correct URL is /api/alert/v1/check (not /stop)
    stopAlert: builder.mutation<any, { alert_name: string }>({
      query: ({ alert_name }) => ({
        url: `/api/alert/v1/check`,
        method: DELETE,
        body: { check_name: alert_name },
        responseHandler: (response: Response) => response.text(),
      }),
      invalidatesTags: ["AlertChecks"],
    }),

    // Create group destination
    createGroupDestination: builder.mutation<
      any,
      { group_name: string; alert_dest_list: { alert_dest_type: string; value: string }[] }
    >({
      query: (payload) => ({
        url: `/api/alert/v1/check/group_destinations`,
        method: POST,
        body: trimStrings(payload),
        responseHandler: (response: Response) => response.text(),
      }),
      invalidatesTags: ["AlertChecks"],
    }),
  }),
});

export const {
  useFetchAlertSchemasQuery,
  useFetchAllChecksQuery,
  useFetchPortfoliosQuery,
  useFetchSlackChannelsQuery,
  useFetchTelegramChannelsQuery,
  useFetchSmsChannelsQuery,
  useFetchGroupDestinationsQuery,
  useStartDefaultConfigMutation,
  useStartAlertMutation,
  useTestFireAlertMutation,
  useStopAlertMutation,
  useCreateGroupDestinationMutation,
} = alertManagerApi;
